import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Zap, Users, Gift, Minus, Plus, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import appLogo from "@/assets/eyecontrol-icon-white.png";
import { useTranslation } from "@/hooks/useTranslation";
import { saveUserSettings, getUserSettings } from "@/lib/userSettings";
import type { Language } from "@/lib/translations";

const LANGUAGES: { code: Language; name: string; flag: string }[] = [
  { code: "he", name: "עברית", flag: "🇮🇱" },
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "ar", name: "العربية", flag: "🇸🇦" },
  { code: "ru", name: "Русский", flag: "🇷🇺" },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { t, lang, dir } = useTranslation();
  const [isYearly, setIsYearly] = useState(false);
  const [groupUsers, setGroupUsers] = useState(5);
  const [showLangMenu, setShowLangMenu] = useState(false);

  // Prices
  const personalMonthly = 100;
  const personalYearly = 80;
  const groupMonthlyPerUser = 80;
  const groupYearlyPerUser = 64;

  const personalPrice = isYearly ? personalYearly : personalMonthly;
  const groupPricePerUser = isYearly ? groupYearlyPerUser : groupMonthlyPerUser;
  const groupTotalPrice = groupPricePerUser * groupUsers;

  const handleStartTrial = () => {
    navigate("/auth");
  };

  const handleLanguageChange = (newLang: Language) => {
    saveUserSettings({ language: newLang });
    setShowLangMenu(false);
    window.location.reload();
  };

  const currentLang = LANGUAGES.find(l => l.code === lang) || LANGUAGES[0];

  const features = [
    t("featureEyeTracking"),
    t("featureCommBoard"),
    t("featureSmartTyping"),
    t("featureCaregiverMsg"),
    t("featureTvYoutube"),
    t("featureGames"),
    t("featureSupport"),
    t("featureUpdates"),
  ];

  return (
    <div className="min-h-screen bg-background" dir={dir}>
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4">
        <button onClick={() => navigate("/")} className="flex items-center gap-2">
          <img src={appLogo} alt="My EyeControl" className="w-10 h-10" />
        </button>
        <div className="flex items-center gap-3">
          {/* Language Switcher */}
          <div className="relative">
            <Button
              variant="ghost"
              onClick={() => setShowLangMenu(!showLangMenu)}
              className="flex items-center gap-2 text-foreground"
            >
              <Globe className="w-5 h-5" />
              <span>{currentLang.flag}</span>
              <span className="hidden sm:inline">{currentLang.name}</span>
            </Button>
            {showLangMenu && (
              <div className="absolute top-full mt-1 end-0 bg-card border border-border rounded-xl shadow-lg z-50 min-w-[160px] overflow-hidden">
                {LANGUAGES.map((language) => (
                  <button
                    key={language.code}
                    onClick={() => handleLanguageChange(language.code)}
                    className={cn(
                      "w-full px-4 py-3 flex items-center gap-3 hover:bg-secondary/50 transition-colors",
                      lang === language.code && "bg-secondary"
                    )}
                  >
                    <span className="text-xl">{language.flag}</span>
                    <span className="text-foreground">{language.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <Button variant="ghost" onClick={() => navigate("/auth")} className="text-foreground">
            {t("login")}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Black Friday Banner */}
        <div className="bg-gradient-to-r from-accent/90 to-accent rounded-2xl p-4 mb-8 text-center">
          <div className="flex items-center justify-center gap-2 text-accent-foreground">
            <Gift className="w-6 h-6" />
            <span className="text-xl font-bold font-['Poppins']">{t("blackFridaySale")}</span>
            <span className="text-2xl">🎉</span>
          </div>
          <p className="text-accent-foreground/90 mt-1">{t("limitedTimeOffer")}</p>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-4">{t("pricingTitle")}</h1>
          <p className="text-muted-foreground text-lg">{t("pricingSubtitle")}</p>
        </div>

        {/* Toggle */}
        <div className="flex items-center justify-center gap-4 mb-10">
          <span className={cn("text-lg font-medium", !isYearly ? "text-foreground" : "text-muted-foreground")}>
            {t("monthly")}
          </span>
          <Switch checked={isYearly} onCheckedChange={setIsYearly} />
          <span className={cn("text-lg font-medium", isYearly ? "text-foreground" : "text-muted-foreground")}>
            {t("yearly")}
            {isYearly && <span className="text-accent mx-2 text-sm">{t("yearlyDiscount")}</span>}
          </span>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Personal Plan */}
          <div className="bg-card rounded-3xl p-6 border border-border relative overflow-hidden">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Zap className="w-6 h-6 text-accent" />
                <h3 className="text-2xl font-bold text-foreground">{t("personal")}</h3>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold text-foreground">₪{personalPrice}</span>
                <span className="text-muted-foreground">{t("perMonth")}</span>
              </div>
              {isYearly && (
                <p className="text-muted-foreground text-sm mt-1">
                  <span className="line-through">₪{personalMonthly}</span>
                </p>
              )}
            </div>

            <Button 
              onClick={handleStartTrial}
              className="w-full h-14 text-lg font-semibold bg-accent hover:bg-accent/90 text-accent-foreground rounded-xl"
            >
              {t("startFreeTrial")}
            </Button>
          </div>

          {/* Group Plan */}
          <div className="bg-card rounded-3xl p-6 border-2 border-primary relative overflow-hidden">
            <div className={cn(
              "absolute top-0 bg-primary text-primary-foreground px-4 py-1 text-sm font-medium",
              dir === "rtl" ? "right-0 rounded-bl-xl" : "left-0 rounded-br-xl"
            )}>
              {t("forTeams")}
            </div>

            <div className="flex items-center justify-between mb-6 mt-4">
              <div className="flex items-center gap-2">
                <Users className="w-6 h-6 text-primary" />
                <h3 className="text-2xl font-bold text-foreground">{t("group")}</h3>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold text-foreground">₪{groupPricePerUser}</span>
                <span className="text-muted-foreground">{t("perUserPerMonth")}</span>
              </div>
              {isYearly && (
                <p className="text-muted-foreground text-sm mt-1">
                  <span className="line-through">₪{groupMonthlyPerUser}</span>
                </p>
              )}
              <p className="text-muted-foreground text-sm mt-2">{t("minUsers")}</p>
            </div>

            {/* User Counter */}
            <div className="flex items-center justify-between bg-secondary/50 rounded-xl p-3 mb-4">
              <span className="text-muted-foreground">{t("users")}</span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setGroupUsers(Math.max(5, groupUsers - 1))}
                  className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center hover:bg-secondary/80 transition-colors"
                  disabled={groupUsers <= 5}
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="text-xl font-bold text-foreground w-8 text-center">{groupUsers}</span>
                <button
                  onClick={() => setGroupUsers(groupUsers + 1)}
                  className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center hover:bg-secondary/80 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Total Price */}
            <div className="flex justify-between items-center mb-4 text-sm">
              <div>
                <span className="text-muted-foreground">{t("regularPrice")}</span>
                <span className="text-muted-foreground line-through mx-2">₪{groupMonthlyPerUser * groupUsers}</span>
              </div>
              <div>
                <span className="text-muted-foreground">{t("salePrice")}</span>
                <span className="text-accent font-bold mx-2">₪{groupTotalPrice}/{t("perMonth")}</span>
              </div>
            </div>

            <Button 
              onClick={handleStartTrial}
              className="w-full h-14 text-lg font-semibold bg-accent hover:bg-accent/90 text-accent-foreground rounded-xl"
            >
              {t("startFreeTrial")}
            </Button>
          </div>
        </div>

        {/* Features */}
        <div className="bg-card rounded-3xl p-8 border border-border">
          <div className={cn("flex items-center gap-2 mb-6", dir === "rtl" ? "justify-end" : "justify-start")}>
            {dir === "rtl" ? (
              <>
                <h3 className="text-xl font-bold text-foreground">{t("whatsIncluded")}</h3>
                <Gift className="w-6 h-6 text-accent" />
              </>
            ) : (
              <>
                <Gift className="w-6 h-6 text-accent" />
                <h3 className="text-xl font-bold text-foreground">{t("whatsIncluded")}</h3>
              </>
            )}
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, index) => (
              <div key={index} className={cn("flex items-center gap-2", dir === "rtl" ? "justify-end" : "justify-start")}>
                {dir === "rtl" ? (
                  <>
                    <span className="text-foreground">{feature}</span>
                    <Check className="w-5 h-5 text-primary flex-shrink-0" />
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-foreground">{feature}</span>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <p className="text-muted-foreground text-lg">
            {t("startWith7Days")}
          </p>
        </div>
      </main>
    </div>
  );
}